/**
 * Push Notifications Service
 * Handles web push notification subscription and management
 */
import { STORAGE_KEYS } from '../constants';
import { getVapidPublicKey, subscribePush, unsubscribePush } from '../api/services/pushService';

const PUSH_STATUS = {
  UNSUPPORTED: 'unsupported',
  PERMISSION_DENIED: 'permission_denied',
  ENABLED: 'enabled',
  DISABLED: 'disabled',
  SYNCING: 'syncing',
  ERROR: 'error',
};

const PUSH_RETRY_DELAYS_MS = [250, 750];
let vapidPublicKeyCache = '';

const buildPreferenceStorageKey = (userId) =>
  `${STORAGE_KEYS.PUSH_ENABLED}:${userId || 'anonymous'}`;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

async function withRetry(operation, retries = PUSH_RETRY_DELAYS_MS.length) {
  let lastError;

  for (let attempt = 0; attempt <= retries; attempt += 1) {
    try {
      return await operation();
    } catch (error) {
      lastError = error;
      if (attempt >= retries) {
        break;
      }
      await sleep(PUSH_RETRY_DELAYS_MS[attempt] || 1000);
    }
  }

  throw lastError;
}

async function loadVapidPublicKey(forceRefresh = false) {
  if (!forceRefresh && vapidPublicKeyCache) {
    return vapidPublicKeyCache;
  }

  if (!forceRefresh) {
    const cached = localStorage.getItem(STORAGE_KEYS.VAPID_PUBLIC_KEY);
    if (cached) {
      vapidPublicKeyCache = cached;
      return cached;
    }
  }

  const publicKey = await withRetry(() => getVapidPublicKey());
  if (!publicKey) {
    throw new Error('VAPID public key is not configured in backend');
  }

  vapidPublicKeyCache = publicKey;
  localStorage.setItem(STORAGE_KEYS.VAPID_PUBLIC_KEY, publicKey);
  return publicKey;
}

/**
 * Check if push notifications are supported
 */
export function isPushSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window;
}

export function getPushPreference(userId) {
  const stored = localStorage.getItem(buildPreferenceStorageKey(userId));
  if (stored === null) {
    return true;
  }
  return stored === 'true';
}

export function setPushPreference(userId, enabled) {
  localStorage.setItem(buildPreferenceStorageKey(userId), String(enabled));
}

export function getPushStatusLabel(status) {
  return status || PUSH_STATUS.DISABLED;
}

/**
 * Request notification permission
 */
export async function requestNotificationPermission() {
  if (!('Notification' in window)) {
    console.log('This browser does not support notifications');
    return 'denied';
  }

  if (Notification.permission === 'granted') {
    return 'granted';
  }

  if (Notification.permission !== 'denied') {
    const permission = await Notification.requestPermission();
    return permission;
  }

  return Notification.permission;
}

/**
 * Get current push subscription
 */
export async function getCurrentSubscription() {
  if (!isPushSupported()) {
    return null;
  }

  // `navigator.serviceWorker.ready` never resolves when no service worker is
  // registered (e.g. the dev build, which doesn't register one). Awaiting it
  // there hangs any caller — including the pre-logout cleanup, which is why the
  // logout button appeared dead in dev. Bail out when there's no registration.
  const existing = await navigator.serviceWorker.getRegistration();
  if (!existing) {
    return null;
  }

  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();
  return subscription;
}

/**
 * Subscribe to push notifications
 */
export async function subscribeToPush() {
  if (!isPushSupported()) {
    throw new Error('Push notifications are not supported');
  }

  const permission = await requestNotificationPermission();
  if (permission !== 'granted') {
    throw new Error('Notification permission denied');
  }

  const registration = await navigator.serviceWorker.ready;

  // Check if already subscribed
  let subscription = await registration.pushManager.getSubscription();

  if (subscription) {
    console.log('Already subscribed to push notifications');
    return subscription;
  }

  // Create new subscription
  const vapidPublicKey = await loadVapidPublicKey();
  const options = {
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
  };

  subscription = await registration.pushManager.subscribe(options);
  console.log('Push notification subscription created');

  return subscription;
}

/**
 * Unsubscribe from push notifications
 */
export async function unsubscribeFromPush() {
  const subscription = await getCurrentSubscription();

  if (subscription) {
    await subscription.unsubscribe();
    console.log('Unsubscribed from push notifications');
    return true;
  }

  return false;
}

/**
 * Send subscription to backend
 */
export async function sendSubscriptionToServer(subscription) {
  const subscriptionData = subscription.toJSON();

  try {
    await withRetry(() => subscribePush({
      endpoint: subscriptionData.endpoint,
      keys: {
        p256dh: subscriptionData.keys.p256dh,
        auth: subscriptionData.keys.auth,
      },
    }));
    console.log('Push subscription sent to server');
    return true;
  } catch (error) {
    console.error('Failed to send subscription to server:', error);
    throw error;
  }
}

/**
 * Remove subscription from backend
 */
export async function removeSubscriptionFromServer(endpoint = '') {
  try {
    await withRetry(() => unsubscribePush(endpoint || undefined));
    console.log('Push subscription removed from server');
    return true;
  } catch (error) {
    console.error('Failed to remove subscription from server:', error);
    throw error;
  }
}

/**
 * Convert VAPID key from base64 to Uint8Array
 */
function urlBase64ToUint8Array(base64String) {
  if (!base64String) {
    throw new Error('VAPID public key is not configured');
  }

  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }

  return outputArray;
}

/**
 * Show a local notification (for testing)
 */
export async function showLocalNotification(title, options = {}) {
  const permission = await requestNotificationPermission();

  if (permission !== 'granted') {
    console.log('Notification permission not granted');
    return;
  }

  const registration = await navigator.serviceWorker.ready;

  await registration.showNotification(title, {
    icon: '/android-chrome-192x192.png',
    badge: '/favicon-32x32.png',
    tag: 'lms-local-notification',
    ...options,
  });
}

export async function disablePushNotifications() {
  const subscription = await getCurrentSubscription();
  if (!subscription) {
    return PUSH_STATUS.DISABLED;
  }

  const endpoint = subscription.endpoint;
  await removeSubscriptionFromServer(endpoint);
  await subscription.unsubscribe();

  return PUSH_STATUS.DISABLED;
}

export async function enablePushNotifications() {
  const subscription = await subscribeToPush();
  await sendSubscriptionToServer(subscription);
  return PUSH_STATUS.ENABLED;
}

export async function getPushStatus() {
  if (!isPushSupported()) {
    return PUSH_STATUS.UNSUPPORTED;
  }

  if (Notification.permission === 'denied') {
    return PUSH_STATUS.PERMISSION_DENIED;
  }

  if (Notification.permission !== 'granted') {
    return PUSH_STATUS.DISABLED;
  }

  const subscription = await getCurrentSubscription();
  return subscription ? PUSH_STATUS.ENABLED : PUSH_STATUS.DISABLED;
}

export async function syncPushSubscription(userId) {
  if (!isPushSupported()) {
    return PUSH_STATUS.UNSUPPORTED;
  }

  if (!getPushPreference(userId)) {
    try {
      await disablePushNotifications();
    } catch (error) {
      console.warn('Failed to enforce push disable preference', error);
    }
    return PUSH_STATUS.DISABLED;
  }

  if (Notification.permission !== 'granted') {
    return Notification.permission === 'denied'
      ? PUSH_STATUS.PERMISSION_DENIED
      : PUSH_STATUS.DISABLED;
  }

  const subscription = await getCurrentSubscription();
  if (!subscription) {
    return PUSH_STATUS.DISABLED;
  }

  await sendSubscriptionToServer(subscription);
  return PUSH_STATUS.ENABLED;
}

export { PUSH_STATUS };

